import { ActionButton, DialogTrigger, Flex, ProgressCircle, Text, View } from '@adobe/react-spectrum';
import { useBreakpoint } from '@react-spectrum/utils';
import SpectrumLabel from '@react/react-spectrum/Label';
import Checkmark from '@spectrum-icons/workflow/Checkmark';
import Close from '@spectrum-icons/workflow/Close';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';

import { excApi, handleApiCall } from '@exchange-frontends/api';
import { Case, SwitchContainer, WithSkeleton, WithTimeout } from '@exchange-frontends/components';
import { useMediaQuery } from '@exchange-frontends/custom-hooks';

import { dateFormatter } from '../../../utils/dateFormatter';
import { UserDataContext } from '../../App';
import { Label } from './RatingsAndReviews';
import { ReplyDialog } from './ReplyDialog';

export const Reply = (props) => {
  const { matchedBreakpoints } = useBreakpoint();
  const isMobileSized = !matchedBreakpoints.includes('L');
  const maxWidth500 = useMediaQuery('(max-width:500px)');
  const { accessToken, profile } = useContext(UserDataContext) ?? {};

  const { reply, reviewId, onDeleteSuccess, onUpdateSuccess } = props;
  const { text, authorName, editedAt, createdAt, id, authorIsDeveloper, isEditable, loading } = reply ?? {};

  const [deletePressed, setDeletePressed] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  const editReply = async (id, text) => {
    try {
      const handler = handleApiCall(excApi.editReply, {
        accessToken,
        authorName,
        text,
        replyId: id,
        imsUserId: profile?.userId,
      });
      const response = await handler.run();
      setStatusMessage({
        type: 'positive',
        message: 'Your reply has been updated.',
      });
      onUpdateSuccess(response);
    } catch (err) {
      setStatusMessage({
        type: 'negative',
        message: err.userMessage,
      });
      console.error(err);
    }
  };

  const handleDeleteReply = async () => {
    try {
      setDeleteLoading(true);
      const handler = handleApiCall(excApi.deleteReply, { accessToken, replyId: id, imsUserId: profile?.userId });
      const response = await handler.run();
      if (response) {
        onDeleteSuccess(id);
      } else {
        throw new Error('Failed deleting the reply. Try again.');
      }
    } catch (err) {
      setStatusMessage({
        type: 'negative',
        message: err.userMessage ?? err.message,
      });
      console.error(err);
      setDeleteLoading(false);
      setDeletePressed(false);
    }
  };

  if (reply || loading) {
    return (
      <Flex
        direction="column"
        gap="size-100"
        marginStart={isMobileSized ? 'size-200' : 'size-500'}
        data-testid="reply-element"
      >
        <WithSkeleton shouldRender={!loading} width={isMobileSized ? '60%' : '40%'}>
          <Flex
            gap="size-150"
            alignItems={isMobileSized ? 'start' : 'center'}
            direction={isMobileSized ? 'column' : 'row'}
          >
            {authorIsDeveloper && <SpectrumLabel variant="blue">Organization reply</SpectrumLabel>}
            <em style={{ overflowWrap: 'anywhere' }}>
              <Label>
                By {authorName} - {editedAt || createdAt ? dateFormatter.format(new Date(editedAt ?? createdAt)) : ''}
              </Label>
            </em>
          </Flex>
        </WithSkeleton>
        <WithSkeleton count={3} shouldRender={!loading}>
          <div style={{ overflowWrap: 'anywhere' }}>
            {text
              ?.split('\n')
              .filter((paragraph) => paragraph.length > 0)
              .map((paragraph, idx) => (
                <p style={{ margin: '0' }} key={`${id}-p-${idx}`}>
                  {paragraph}
                </p>
              ))}
          </div>
        </WithSkeleton>
        {isEditable && (
          <Flex direction="row" alignItems="center" gap={isMobileSized ? 'size-100' : 'size-200'} maxHeight="21px">
            <Flex gap={isMobileSized ? 'size-100' : 'size-200'} alignItems="center">
              <DialogTrigger>
                <ActionButton isQuiet isDisabled={deletePressed} maxHeight="size-300" marginStart="-4px">
                  <Label>Edit</Label>
                </ActionButton>
                {(close) => (
                  <ReplyDialog close={close} variant="edit" reviewId={reviewId} reply={reply} editReply={editReply} />
                )}
              </DialogTrigger>

              <ActionButton
                isQuiet
                maxHeight="size-300"
                isDisabled={deletePressed}
                onPress={() => {
                  setDeletePressed(true);
                }}
              >
                <Label>Delete</Label>
              </ActionButton>
            </Flex>
            <WithTimeout setShouldShow={() => setStatusMessage(null)} shouldShow={statusMessage !== null}>
              <Flex alignItems="center" gap="size-100">
                <SwitchContainer>
                  <Case when={statusMessage?.type === 'positive'}>
                    <Checkmark color="positive" width="18px" />
                  </Case>
                  <Case when={statusMessage?.type === 'negative'}>
                    <Close color="positive" width="18px" />
                  </Case>
                </SwitchContainer>
                <Label>{statusMessage?.message}</Label>
              </Flex>
            </WithTimeout>
          </Flex>
        )}
        {deletePressed && (
          <Flex alignItems={'center'} gap="size-100" direction={maxWidth500 ? 'column' : 'row'}>
            <Label $centered>Are you sure you want to delete your reply? </Label>
            <Flex gap="size-100">
              <ActionButton onPress={handleDeleteReply} isDisabled={deleteLoading}>
                <Flex marginX="size-50">
                  {deleteLoading && (
                    <ProgressCircle size="S" isIndeterminate marginEnd="size-100" aria-label="Deleting your reply" />
                  )}
                  <Label>Yes, delete</Label>
                </Flex>
              </ActionButton>
              <ActionButton
                isDisabled={deleteLoading}
                onPress={() => {
                  setDeletePressed(false);
                }}
              >
                <View paddingX="size-50">
                  <Label>Cancel</Label>
                </View>
              </ActionButton>
            </Flex>
          </Flex>
        )}
      </Flex>
    );
  } else {
    return null;
  }
};

Reply.propTypes = {
  reviewId: PropTypes.string,
  reply: PropTypes.object,
  onDeleteSuccess: PropTypes.func,
  onUpdateSuccess: PropTypes.func,
};
